import { useMemo } from 'react';

import { ChevronDown } from '@mint-lib/icons';
import {
  Chip,
  Select,
  SelectContent,
  SelectItem,
  SelectTriggerUnstyled,
  SelectValue,
} from '@mint-lib/tw-components';

export type ChipSelectOption = {
  value: string;
  label: string;
};

export type Props = {
  options: ChipSelectOption[];
  placeholder?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  renderValue?: (value: string, option?: ChipSelectOption) => string;
  isDisabled?: boolean;
};

const ChipSelect = ({
  options,
  placeholder,
  value,
  onValueChange,
  renderValue,
  isDisabled = false,
}: Props) => {
  const displayText = useMemo(() => {
    if (!value || !renderValue) return undefined;

    const selectedOption = options.find((o) => o.value === value);

    return renderValue(value, selectedOption);
  }, [value, renderValue, options]);

  return (
    <Select value={value} onValueChange={onValueChange} disabled={isDisabled}>
      <SelectTriggerUnstyled asChild>
        <Chip
          color={value ? 'tertiary' : 'secondary'}
          variant={value ? 'filled' : 'outline'}
          size="large"
          disabled={isDisabled}
        >
          {displayText ?? <SelectValue placeholder={placeholder} />}
          <ChevronDown />
        </Chip>
      </SelectTriggerUnstyled>
      <SelectContent className="z-popover min-w-28">
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export { ChipSelect };
